import { Link, useTheme } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DSBox from "../system-design/DSBox";
import DSTypography from "../system-design/Typography/DSTypography";
import DSPaper from "../system-design/DSPaper";
import DSIconButton from "../system-design/DSIconButton";
import { isIOS, isMobile } from "react-device-detect";
import { useState } from "react";
import DSSwitch from "../system-design/DSSwitch";
import GiftView from "./GiftView";
import { KLYDO_YELLOW } from "../../consts/colors";
import useAppContext from "../../hooks/useAppContext";
import { AlertType } from "../system-design/DSAlert";
import useGiftQuery from "./hooks/useGiftQuery";
import { useHistory } from "../../utils";
import { SplashScreen } from "../../LoginView/SplashScreen";
import GreetLogo from "../system-design/GreetLogo";
import { useGiftContext } from "./Context/GiftContextProvider";
import { useNavigate } from "react-router";
import { WizardStep } from "../../consts/wizardSteps";
import { ReactComponent as EditIcon } from "../system-design/icons/edit-icon.svg";
import { PopulatedGift } from "../../api/gift";

const GiftDone = () => {
  const theme = useTheme();
  const { search } = useHistory();
  const { setKlydoGiftProps, setSelectedKlydoGreet } = useGiftContext();
  const navigate = useNavigate();

  const { data: giftInfo, isLoading: isLoadingGift } = useGiftQuery({
    orderName: (search.orderName || search.orderId) as string,
    clockId: search.clockId as string,
  });
  const gift: PopulatedGift = giftInfo?.gift;
  const clockInfo = giftInfo?.clockInfo;

  const selectedKlydoGreet = gift?.klydo;
  const klydoGiftProps = {
    senderImageUrl: gift?.senderImageUrl,
    senderName: gift?.senderName,
    date: gift?.date,
    greetingContent: gift?.greetingContent,
    pendulumLogoUrl: gift?.pendulumLogoUrl,
    orderName: gift?.orderName || "",
    clockId: clockInfo?.id || "",
    klydoId: gift?.klydoId,
    timeZone: gift?.timeZone,
    giftId: gift?.giftId,
  };
  const { showAlert } = useAppContext();
  const [showGreeting, setShowGreeting] = useState(false);

  const isClockRegistered = clockInfo?.registerProduct;

  const handleEditGreet = () => {
    if (isClockRegistered) {
      return;
    }
    setKlydoGiftProps(klydoGiftProps);
    if (selectedKlydoGreet) {
      setSelectedKlydoGreet(selectedKlydoGreet);
    }

    const currentParams = new URLSearchParams();
    const orderNameValue = search.orderName || search.orderId;
    if (orderNameValue)
      currentParams.set("orderName", orderNameValue as string);
    if (search.clockId) currentParams.set("clockId", search.clockId as string);
    currentParams.set("step", WizardStep.OCCASION);

    navigate(`/gift/wizard?${currentParams.toString()}`, { replace: true });
  };

  const getShareableLink = () => {
    const baseUrl = "editor.klydoclock.com/gift/share/";
    if (klydoGiftProps?.orderName) {
      return `${baseUrl}?orderName=${klydoGiftProps.orderName}`;
    } else if (klydoGiftProps?.clockId) {
      return `${baseUrl}?orderName=${klydoGiftProps.clockId}`;
    }
    return window.location.href;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareableLink());
    showAlert({
      message: "Link copied to clipboard!",
      alertType: AlertType.SUCCESS,
    });
  };

  const handleToggle = (event) => {
    setShowGreeting(event.target.checked);
  };

  const displayorderNameOrClockId = () => {
    if (gift?.orderName === clockInfo?.id && clockInfo?.idf) {
      return `Clock ID: ${clockInfo?.idf}`;
    } else if (gift?.orderName) {
      return `Order Name: ${gift.orderName}`;
    }
    return "";
  };

  if (isLoadingGift) return SplashScreen();

  return (
    <DSPaper
      style={{
        height: "100vh",
        overflow: "scroll",
      }}
    >
      <DSBox
        color="transparent"
        sx={{
          backgroundColor: theme.palette.background.paper,
          paddingY: 4,
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          boxShadow: "none",
        }}
      >
        <GreetLogo color={KLYDO_YELLOW} />
      </DSBox>
      <DSBox
        sx={{
          flexDirection: "column",
          justifyContent: "center",
          textAlign: "center",
          alignItems: "center",
          backgroundColor: theme.palette.background.paper,
          height: "100vh",
        }}
      >
        <DSTypography
          fontSize={17}
          fontFamily={"Gilroy-Medium"}
          style={{
            textAlign: isMobile ? "center" : "right",
            paddingRight: isMobile ? 0 : "20%",
          }}
        >
          {displayorderNameOrClockId()}
        </DSTypography>
        <DSBox
          gap={isMobile ? 0.5 : 20}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row-reverse" },
            paddingTop: { md: 8 },
            justifyContent: "center",
            textAlign: "center",
            alignItems: "center",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <DSBox
            sx={{
              flexDirection: "column",
              paddingTop: { md: 2 },
            }}
          >
            <DSBox>
              <DSTypography
                lineHeight={1}
                fontStyle={"bold"}
                fontSize={47.52}
                fontFamily={"Gilroy-Bold"}
              >
                Your KlydoGreet
              </DSTypography>

              <DSTypography
                lineHeight={1}
                fontStyle={"bold"}
                fontSize={47.52}
                fontFamily={"Gilroy-Bold"}
              >
                is ready!
                {!isClockRegistered && (
                  <DSIconButton
                    title={"Edit greet"}
                    onClick={handleEditGreet}
                    disabled={isClockRegistered}
                    sx={{
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      "&:disabled": {
                        color: "#A5A5A5",
                      },
                    }}
                  >
                    <EditIcon />
                  </DSIconButton>
                )}
              </DSTypography>
            </DSBox>
            <DSBox py={1} />
            <DSBox sx={{ paddingBottom: { xs: 2 } }}>
              <DSTypography fontSize={17} fontFamily={"Gilroy-Medium"}>
                Your personalized KlydoGreet has been
              </DSTypography>
              <DSTypography fontSize={17} fontFamily={"Gilroy-Medium"}>
                set on the clock that's about to
              </DSTypography>
              <DSTypography fontSize={17} fontFamily={"Gilroy-Medium"}>
                become a very special gift.
              </DSTypography>

              <DSTypography fontSize={17} fontFamily={"Gilroy-Medium"}>
                We can't wait to hear
              </DSTypography>
              <DSTypography fontSize={17} fontFamily={"Gilroy-Medium"}>
                all about their reaction!
              </DSTypography>
            </DSBox>

            {!isMobile && (
              <>
                <DSBox
                  onClick={handleCopyLink}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    flexDirection: "row",
                    cursor: "pointer",
                    paddingBottom: 2,
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 20, color: KLYDO_YELLOW }} />

                  <DSTypography
                    color={KLYDO_YELLOW}
                    fontSize={20}
                    fontFamily={"Gilroy-Medium"}
                  >
                    share your greet with anyone
                  </DSTypography>
                </DSBox>
                <DSBox>
                  <DSTypography
                    mb={1}
                    fontSize={15}
                    fontFamily={"Gilroy-Regular"}
                  >
                    Have questions or something to share?
                  </DSTypography>
                  <DSTypography
                    mb={1}
                    fontSize={15}
                    fontFamily={"Gilroy-Regular"}
                  >
                    We'd love to hear from you!
                  </DSTypography>
                  <DSTypography
                    mb={1}
                    fontSize={15}
                    fontFamily={"Gilroy-Regular"}
                  >
                    <Link
                      color="inherit"
                      href="https://www.klydoclock.com/pages/contact-us"
                    >
                      Contact Us
                    </Link>
                  </DSTypography>
                </DSBox>
              </>
            )}
          </DSBox>

          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: isIOS && isMobile ? "250px" : "290px",
                height: isIOS && isMobile ? "420px" : "483px",
                position: "relative",
              }}
            >
              <GiftView
                gift={klydoGiftProps}
                klydo={selectedKlydoGreet}
                isToggled={showGreeting}
              />
            </div>
            <DSBox
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                width: "100%",
              }}
            >
              <DSTypography
                fontFamily={"Gilroy-SemiBold"}
                fontSize={"16px"}
                color={showGreeting ? "#A5A5A5" : "#FFFFFF"}
              >
                Greet
              </DSTypography>

              <DSSwitch checked={showGreeting} onChange={handleToggle} />

              <DSTypography
                fontFamily={"Gilroy-SemiBold"}
                fontSize={"16px"}
                color={showGreeting ? "#FFFFFF" : "#A5A5A5"}
              >
                Message
              </DSTypography>
            </DSBox>
          </DSBox>
          <DSBox py={1} />

          {isMobile && (
            <>
              <DSBox
                onClick={handleCopyLink}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  flexDirection: "row",
                  cursor: "pointer",
                  paddingBottom: 2,
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 20, color: KLYDO_YELLOW }} />

                <DSTypography
                  color={KLYDO_YELLOW}
                  fontSize={20}
                  fontFamily={"Gilroy-Medium"}
                >
                  share your greet with anyone
                </DSTypography>
              </DSBox>
              <DSBox>
                <DSTypography
                  mb={1}
                  fontSize={15}
                  fontFamily={"Gilroy-Regular"}
                >
                  Have questions or something to share?
                </DSTypography>
                <DSTypography
                  mb={1}
                  fontSize={15}
                  fontFamily={"Gilroy-Regular"}
                >
                  We'd love to hear from you!
                </DSTypography>
                <DSTypography
                  mb={1}
                  pb={2}
                  fontSize={15}
                  fontFamily={"Gilroy-Regular"}
                >
                  <Link
                    color="inherit"
                    href="https://www.klydoclock.com/pages/contact-us"
                  >
                    Contact Us
                  </Link>
                </DSTypography>
              </DSBox>
            </>
          )}
        </DSBox>
      </DSBox>
    </DSPaper>
  );
};
export default GiftDone;
